/**
 * Reports view.
 *
 * Owns the Reports tab: launcher chips that open the standalone reports
 * application (`/reports/index.html?report=<key>`), and — for root — a
 * "Report access" config dialog that sets which roles may see each report
 * (stored server-side in report_access_config; root always sees all).
 *
 * Launcher visibility follows the same config: chips for reports the current
 * user can't access are hidden, matching the reports window.
 */

import { escapeHtml } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const showFlash = (msg, type) => notify.showFlash(msg, type);

let initialized = false;

async function gateLaunchers() {
  // Hide launcher chips for reports the user can't access (root sees all).
  const reportsView = document.getElementById('reportsView');
  if (!reportsView) return;
  let keys = null;
  try {
    const acc = await api.fetch(API.adminReportsAccessible, { method: 'GET' });
    if (Array.isArray(acc.keys)) keys = new Set(acc.keys);
  } catch {
    keys = null; // on error, leave every chip visible (server still enforces)
  }
  if (!keys) return;
  reportsView.querySelectorAll('button[data-report]').forEach((btn) => {
    const key = btn.getAttribute('data-report') || '';
    if (!key) return; // "Browse all" — always shown
    btn.hidden = !keys.has(key);
  });
  // The dedicated CRM installations chip isn't a data-report button.
  const crmBtn = document.getElementById('openCrmInstallReport');
  if (crmBtn) crmBtn.hidden = !keys.has('crm_installations');
}

// ---- Report access config (root only) --------------------------------------

let accessRoles = [];      // all assignable role names
let accessItems = [];      // [{key, title, default_roles, roles, ...}]

async function loadAccessConfig() {
  const table = document.getElementById('reportAccessTable');
  const status = document.getElementById('reportAccessStatus');
  if (!table) return;
  if (status) status.textContent = 'Loading…';
  try {
    const [cfg, rolesResp] = await Promise.all([
      api.fetch(API.adminReportAccessConfig, { method: 'GET' }),
      api.fetch(API.roles, { method: 'GET' }),
    ]);
    accessItems = Array.isArray(cfg.items) ? cfg.items : [];
    const rawRoles = Array.isArray(rolesResp) ? rolesResp : (rolesResp.items || rolesResp.roles || []);
    accessRoles = rawRoles
      .map((r) => (typeof r === 'string' ? r : (r.name || r.role || '')))
      .filter((r) => r && r !== 'root')
      .sort();
    renderAccessTable();
    if (status) status.textContent = '';
  } catch (err) {
    if (status) status.textContent = (err && err.message) ? err.message : 'Failed to load report access.';
  }
}

function renderAccessTable() {
  const table = document.getElementById('reportAccessTable');
  if (!table) return;
  const head = `<thead><tr>
      <th style="position:sticky; left:0; background:var(--card); text-align:left;">Report</th>
      <th title="Always on">root</th>
      ${accessRoles.map((r) => `<th style="white-space:nowrap;">${escapeHtml(r)}</th>`).join('')}
    </tr></thead>`;
  const body = accessItems.map((item) => {
    const allowed = new Set(item.roles || []);
    const cells = accessRoles.map((r) => {
      const checked = allowed.has(r) ? 'checked' : '';
      return `<td style="text-align:center;"><input type="checkbox" data-report-key="${escapeHtml(item.key)}" data-role="${escapeHtml(r)}" ${checked} /></td>`;
    }).join('');
    const overridden = item.is_overridden ? ' <span title="Changed from default" style="color:var(--warn,#c93);">•</span>' : '';
    return `<tr>
      <td style="position:sticky; left:0; background:var(--card); text-align:left;"><strong>${escapeHtml(item.title)}</strong>${overridden}</td>
      <td style="text-align:center;"><input type="checkbox" checked disabled title="root always sees every report" /></td>
      ${cells}
    </tr>`;
  }).join('');
  table.innerHTML = head + `<tbody>${body}</tbody>`;
}

async function saveAccessConfig() {
  const table = document.getElementById('reportAccessTable');
  const saveBtn = document.getElementById('reportAccessSaveBtn');
  if (!table) return;
  // Collect the ticked roles per report from the current checkbox state.
  const selected = {};
  accessItems.forEach((item) => { selected[item.key] = []; });
  table.querySelectorAll('input[type="checkbox"][data-report-key]').forEach((cb) => {
    if (cb.checked) selected[cb.dataset.reportKey].push(cb.dataset.role);
  });
  // Only PUT the reports whose set actually changed.
  const changed = accessItems.filter((item) => {
    const now = [...selected[item.key]].sort().join(',');
    const before = [...(item.roles || [])].sort().join(',');
    return now !== before;
  });
  if (!changed.length) { showFlash('No changes to save.'); return; }
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }
  try {
    for (const item of changed) {
      await api.fetch(API.adminReportAccessConfigSet(item.key), {
        method: 'PUT',
        body: JSON.stringify({ roles: selected[item.key] }),
      });
    }
    showFlash(`Saved access for ${changed.length} report(s).`);
    await loadAccessConfig();   // refresh (updates the overridden markers)
    await gateLaunchers();      // reflect any change to the current user's own access
  } catch (err) {
    showFlash((err && err.message) ? err.message : 'Failed to save report access.', 'error');
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save changes'; }
  }
}

export function init() {
  if (initialized) return;
  initialized = true;

  const openCrmInstallReport = document.getElementById('openCrmInstallReport');
  if (openCrmInstallReport) {
    openCrmInstallReport.addEventListener('click', () => {
      window.open('/reports/index.html?report=crm_installations', '_blank', 'noopener');
    });
  }

  const reportsView = document.getElementById('reportsView');
  if (reportsView) {
    // Keep the launcher chips alphabetical by title (emoji stripped); ghost
    // buttons ("Browse all", "Report access") pinned last.
    const row = reportsView.querySelector('.row');
    if (row) {
      const label = (b) => b.textContent.trim().replace(/^[^\p{L}]+/u, '').toLowerCase();
      const isGhost = (b) => b.classList.contains('ghost');
      Array.from(row.querySelectorAll('button'))
        .sort((a, b) => {
          if (isGhost(a) !== isGhost(b)) return isGhost(a) ? 1 : -1;
          return label(a).localeCompare(label(b));
        })
        .forEach((b) => row.appendChild(b));
    }
    reportsView.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-report]');
      if (!btn) return;
      const key = btn.getAttribute('data-report') || '';
      const url = key ? `/reports/index.html?report=${encodeURIComponent(key)}` : '/reports/index.html';
      window.open(url, '_blank', 'noopener');
    });
  }

  // Root-only "Report access" config.
  const openBtn = document.getElementById('openReportAccessBtn');
  if (openBtn && hasRole('root')) {
    openBtn.hidden = false;
    openBtn.addEventListener('click', async () => {
      openDialog(document.getElementById('reportAccessDlg'));
      await loadAccessConfig();
    });
  }
  const saveBtn = document.getElementById('reportAccessSaveBtn');
  if (saveBtn) saveBtn.addEventListener('click', saveAccessConfig);

  gateLaunchers();
}

export default { init };
